// S4 — the tenant list (spec 15 §4). Search by name or subdomain, filter by
// status, page by cursor, click a row for S5.
//
// Columns are what the API returns: name, subdomain, status, the health dot
// (M15-4, D-612), industry pack, plan, this month's active users (M15-6,
// M15-7a), region and created. The spec's outstanding balance waits for the
// billing store (§0.6), so it is absent rather than shown as a dash.

import { enumLabel } from "../../../lib/enum-label";
import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { Field } from "../../../components/field";
import { Select } from "../../../components/select";
import { EmptyState, ErrorState, TableSkeleton } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { formatTimestamp } from "../../../lib/datetime";
import { useDebouncedValue } from "../../../lib/use-debounced-value";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import {
  listTenants,
  listVerticalPacks,
  type PlatformMe,
  type TenantSummary,
} from "../lib/platform-api";
import { canCreateTenant, statusVariant, TENANT_STATUSES } from "../lib/tenants";
import { packLabel } from "../lib/labels";
import { HealthDot } from "../components/health-dot";

export const TENANT_PAGE_SIZE = 25;

export function TenantListScreen({
  me,
  initialStatus = "",
}: {
  me: PlatformMe;
  /** The status the list opens filtered by (`?status=`, from a fleet tile). */
  initialStatus?: string;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(initialStatus);
  // The cursor that OPENED each page: [undefined] is page one, and each Next
  // pushes the server's next_cursor. Back pops. A new search or filter starts
  // over at page one, because an old cursor means nothing under a new filter.
  const [cursors, setCursors] = useState<readonly (string | undefined)[]>([undefined]);
  const q = useDebouncedValue(search.trim(), 300);
  const cursor = cursors[cursors.length - 1];
  const page = cursors.length;

  const tenants = useQuery({
    queryKey: ["platform", "tenants", { q, status, cursor }],
    queryFn: ({ signal }) =>
      listTenants(
        {
          limit: TENANT_PAGE_SIZE,
          ...(q !== "" ? { q } : {}),
          ...(status !== "" ? { status } : {}),
          ...(cursor !== undefined ? { cursor } : {}),
        },
        signal,
      ),
    placeholderData: keepPreviousData,
  });
  // Pack names only label a column; the list works without them.
  const packs = useQuery({
    queryKey: ["platform", "vertical-packs"],
    queryFn: ({ signal }) => listVerticalPacks(signal),
    staleTime: 5 * 60_000,
  });
  useSignInAgainOn401(tenants.error);

  const filtered = q !== "" || status !== "";
  const creator = canCreateTenant(me.role);

  const columns: DataTableColumn<TenantSummary>[] = [
    {
      key: "name",
      header: t("admin.tenants.colName"),
      render: (row) => (
        <span className="admin-name-cell">
          <span className="admin-strong">{row.name}</span>
          {row.sandbox ? (
            <StatusChip variant="accent" dot={false} data-testid="admin-tenant-sandbox">
              {t("admin.tenants.sandbox")}
            </StatusChip>
          ) : null}
        </span>
      ),
    },
    {
      key: "subdomain",
      header: t("admin.tenants.colSubdomain"),
      id: true,
      render: (row) => <code>{row.subdomain}</code>,
    },
    {
      key: "status",
      header: t("admin.tenants.colStatus"),
      render: (row) => (
        <StatusChip variant={statusVariant(row.status)} data-testid="admin-tenant-status">
          {enumLabel(t, "admin.status", row.status)}
        </StatusChip>
      ),
    },
    {
      key: "health",
      header: t("admin.health.colHealth"),
      kind: "status",
      render: (row) => <HealthDot health={row.health} data-testid="admin-tenant-health" />,
    },
    {
      key: "pack",
      header: t("admin.tenants.colPack"),
      render: (row) => packLabel(row.vertical_pack, packs.data, i18n.language),
    },
    // M15-6 and M15-7a: the plan, and this month's active users once the
    // metering worker has written them (a dash-free empty cell until then).
    {
      key: "plan",
      header: t("admin.tenantEdit.colPlan"),
      render: (row) => <span data-testid="admin-tenant-plan-cell">{row.plan_name}</span>,
    },
    {
      key: "mau",
      header: t("admin.tenantEdit.colMau"),
      render: (row) =>
        row.mau === null ? null : (
          <span className="num" data-testid="admin-tenant-mau">
            {new Intl.NumberFormat(i18n.language).format(row.mau)}
          </span>
        ),
    },
    {
      key: "region",
      header: t("admin.tenants.colRegion"),
      render: (row) => enumLabel(t, "admin.regions", row.region),
    },
    {
      key: "created",
      header: t("admin.tenants.colCreated"),
      render: (row) => formatTimestamp(row.created_at, i18n.language),
    },
  ];

  function resetPaging() {
    setCursors([undefined]);
  }

  let body;
  if (tenants.isPending) {
    body = <TableSkeleton rows={6} cols={5} label={t("admin.tenants.loading")} />;
  } else if (tenants.isError) {
    body = (
      <ErrorState
        title={t("admin.load.failed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void tenants.refetch();
        }}
        requestId={requestIdOf(tenants.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else if (tenants.data.items.length === 0 && page === 1) {
    body = filtered ? (
      <EmptyState
        title={t("admin.tenants.noMatchTitle")}
        body={t("admin.tenants.noMatchBody")}
        ctaLabel={t("admin.tenants.clearFilters")}
        onCta={() => {
          setSearch("");
          setStatus("");
          resetPaging();
        }}
      />
    ) : (
      <EmptyState
        title={t("admin.tenants.emptyTitle")}
        body={creator ? t("admin.tenants.emptyBody") : t("admin.tenants.emptyBodyViewer")}
        {...(creator
          ? {
              ctaLabel: t("admin.tenants.create"),
              onCta: () => {
                void navigate({ to: "/console/tenants/new" });
              },
            }
          : {})}
      />
    );
  } else {
    const nextCursor = tenants.data.next_cursor;
    // While a page is loading, the rows on screen are the previous page's
    // (keepPreviousData), and so is this next_cursor: a second Next would push
    // the cursor already pushed and label the old rows with the new number.
    const settled = !tenants.isPlaceholderData && !tenants.isFetching;
    body = (
      <DataTable
        columns={columns}
        rows={tenants.data.items}
        rowKey={(row) => row.id}
        empty={null}
        caption={t("admin.tenants.title")}
        rowTestId="admin-tenant-row"
        rowProps={(row) => ({ "data-subdomain": row.subdomain })}
        onRowClick={(row) => {
          void navigate({ to: "/console/tenants/$tenantId", params: { tenantId: row.id } });
        }}
        pager={{
          range: t("admin.tenants.page", { page }),
          page,
          onPage: (next) => {
            if (next > page && settled && nextCursor !== null) setCursors([...cursors, nextCursor]);
            else if (next < page) setCursors(cursors.slice(0, -1));
          },
          prevLabel: t("admin.tenants.prev"),
          nextLabel: t("admin.tenants.next"),
          hasPrev: page > 1,
          hasNext: settled && nextCursor !== null,
        }}
      />
    );
  }

  return (
    <section className="admin-screen" data-testid="admin-tenant-list">
      <PageHeader
        title={t("admin.tenants.title")}
        actions={
          creator ? (
            <Button
              variant="primary"
              size="sm"
              data-testid="admin-tenant-create"
              onClick={() => {
                void navigate({ to: "/console/tenants/new" });
              }}
            >
              {t("admin.tenants.create")}
            </Button>
          ) : undefined
        }
      />
      <div className="admin-filters">
        <Field label={t("admin.tenants.search")} wide>
          <input
            className="ds-input"
            type="search"
            value={search}
            placeholder={t("admin.tenants.searchPlaceholder")}
            data-testid="admin-tenant-search"
            onChange={(e) => {
              setSearch(e.target.value);
              resetPaging();
            }}
          />
        </Field>
        <Field label={t("admin.tenants.status")}>
          <Select
            value={status}
            data-testid="admin-tenant-status-filter"
            onChange={(e) => {
              setStatus(e.target.value);
              resetPaging();
            }}
          >
            <option value="">{t("admin.tenants.allStatuses")}</option>
            {TENANT_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`admin.status.${s}`)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      {body}
    </section>
  );
}
